import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Heart,
  MessageCircle,
  Share2,
  Star,
  type LucideIcon,
} from 'lucide-react';
import { TOP_POSTS_LIMIT, type TopPost, type TopPostSort } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { cn } from '../../lib/cn';
import { formatMetric } from '../../lib/display';
import { useTenantKey } from '../../lib/useTenantKey';
import { Card } from '../../components/ui/Card';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { EDGE_BLEED_FRAME, useEdgeBleed } from '../../components/ui/useEdgeBleed';
import { PLATFORM_META } from '../social/platforms';

/**
 * Top Performing Posts — what is actually resonating, inside the window and account selection the
 * rest of the Analytics page is already showing.
 *
 * No filters of its own. The page owns the account and range, and a second set of controls here
 * would let the table disagree with the chart above it about which posts are even in scope.
 *
 * SORTING IS A QUERY, not a client-side reorder. The five most-liked posts and the five most-shared
 * posts are different fives, so re-sorting five rows in the browser would reorder the wrong set and
 * look like it had worked. Each sort is its own request and its own cache entry.
 */

const COLUMNS: { key: TopPostSort; label: string; Icon: LucideIcon }[] = [
  { key: 'likes', label: 'Likes', Icon: Heart },
  { key: 'comments', label: 'Comments', Icon: MessageCircle },
  { key: 'shares', label: 'Shares', Icon: Share2 },
];

export function TopPostsCard({
  account,
  range,
}: {
  /** The page's account filter — a connection id, or undefined for every connected account. */
  account?: string;
  /** The page's trailing window, in days. */
  range: number;
}) {
  const tenantKey = useTenantKey();
  const [params, setParams] = useSearchParams();
  const [sort, setSort] = useState<TopPostSort>('likes');

  const query = useQuery({
    queryKey: ['analytics-top-posts', tenantKey, account ?? 'ALL', range, sort],
    queryFn: ({ signal }) => api.getTopPosts({ account, range, sort }, signal),
    enabled: tenantKey !== '',
  });

  const posts = query.data?.posts ?? [];
  // On a desktop screen too narrow for the table, the whole card runs off the page's right edge and
  // the table's box reaches through the card's padding to that edge, so the cut says it scrolls.
  const bleedRef = useEdgeBleed<HTMLDivElement>('marked');

  /**
   * Open the post in the site-wide composer modal, which renders a published post read-only —
   * caption, media, platform, publish date and its engagement, all already there. Reused rather
   * than rebuilt: a second post viewer is a second thing to keep in step with the first.
   */
  const openPost = (postId: string) => {
    const next = new URLSearchParams(params);
    next.set('compose', postId);
    setParams(next, { replace: false });
  };

  return (
    <Card {...EDGE_BLEED_FRAME} className="mt-4 p-5">
      <div className="flex items-center gap-2.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-tint text-tint-fg">
          <Star className="h-[1.125rem] w-[1.125rem]" aria-hidden />
        </span>
        <h2 className="text-base font-semibold text-ink">Top performing posts</h2>
        <span className="ml-auto text-xs font-medium uppercase tracking-wide text-ink-faint">
          Last {range} days
        </span>
      </div>

      {query.isLoading ? (
        <LoadingState label="Loading posts…" />
      ) : query.isError ? (
        <ErrorState
          message={
            query.error instanceof ApiRequestError
              ? query.error.message
              : 'Could not load top posts.'
          }
          onRetry={() => void query.refetch()}
        />
      ) : posts.length === 0 ? (
        <EmptyState
          title="No posts to show for this range yet"
          description={
            query.data?.notice ??
            'Published posts appear here once their platform reports engagement for them.'
          }
        />
      ) : (
        <div ref={bleedRef} className="mt-4 overflow-x-auto rounded-xl border border-line">
          <table className="w-full min-w-[45rem] border-collapse text-sm">
            <thead>
              <tr className="border-b border-line bg-page text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                <th className="px-3 py-2.5 font-semibold">Caption</th>
                <th className="px-3 py-2.5 font-semibold">Platform</th>
                {COLUMNS.map((column) => (
                  <SortableTh
                    key={column.key}
                    column={column}
                    active={sort === column.key}
                    onSort={() => setSort(column.key)}
                  />
                ))}
                <th className="px-3 py-2.5 text-right font-semibold">Details</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => (
                <Row key={post.postId} post={post} sort={sort} onView={() => openPost(post.postId)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Said out loud rather than padded: a short list is the real answer, and a table that
          silently shows three rows where it usually shows five reads as a bug. Outside the table's
          frame, where the card's other footnotes sit. */}
      {!query.isLoading && !query.isError && posts.length > 0 && posts.length < TOP_POSTS_LIMIT && (
        <p className="mt-3 text-xs text-ink-faint">
          {posts.length === 1
            ? 'One post has engagement data in this range.'
            : `Only ${posts.length} posts have engagement data in this range.`}
        </p>
      )}
    </Card>
  );
}

function Row({ post, sort, onView }: { post: TopPost; sort: TopPostSort; onView: () => void }) {
  const meta = PLATFORM_META[post.platform];
  // What stands in the Caption cell of a post with no text of its own - one that went out before
  // text was required: its internal title, marked as one. The title is never passed off as the
  // caption, and never shown beside one.
  const untitled = `${post.title || 'Untitled post'} (no caption)`;
  return (
    <tr className="border-b border-line-soft last:border-0">
      {/* A caption is one line, however long the post was: this is a ranking table, not a reader. */}
      <td className="max-w-[17.5rem] truncate px-3 py-3 text-ink" title={post.caption || untitled}>
        {post.caption || <span className="italic text-ink-muted">{untitled}</span>}
      </td>
      <td className="px-3 py-3">
        {/* Which ACCOUNT the post went out through, beside its platform's icon: with two Pages
            connected, "Facebook" alone does not say whose post this is. A post that is not tied
            to an account shows the platform's name, as before. */}
        <span
          className={cn(
            'inline-flex max-w-[12.5rem] items-center gap-1.5 rounded-full border border-line-input bg-card px-2.5 py-1 text-xs font-medium',
            meta.ink,
          )}
          title={post.accountName ? `${meta.label} - ${post.accountName}` : undefined}
        >
          <meta.Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {/* The glyph keeps its platform colour in both themes. The name turns neutral in dark:
              on the dark card the platform colours are too faint for 12px text (LinkedIn 2.65:1). */}
          <span className="min-w-0 truncate dark:text-ink-strong">{post.accountName || meta.label}</span>
        </span>
      </td>
      {COLUMNS.map((column) => (
        <td
          key={column.key}
          className={cn(
            'whitespace-nowrap px-3 py-3 text-right tabular-nums',
            // The column being ranked on is the one the eye should land in.
            sort === column.key ? 'font-semibold text-ink' : 'text-ink-muted',
          )}
        >
          {/* An em dash, never a 0: null here means the platform does not report that metric with
              the access we hold, which is not the same as nobody engaging. */}
          {formatMetric(post[column.key])}
        </td>
      ))}
      <td className="whitespace-nowrap px-3 py-3 text-right">
        <button
          type="button"
          onClick={onView}
          className="focus-ring inline-flex items-center gap-1 rounded font-medium text-link hover:text-action-hover"
        >
          View
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </button>
      </td>
    </tr>
  );
}

/** A metric header — click to re-rank. The active column carries the action colour and the chevron. */
function SortableTh({
  column,
  active,
  onSort,
}: {
  column: { key: TopPostSort; label: string; Icon: LucideIcon };
  active: boolean;
  onSort: () => void;
}) {
  return (
    <th
      className="px-3 py-2.5 text-right font-semibold"
      aria-sort={active ? 'descending' : 'none'}
    >
      <button
        type="button"
        onClick={onSort}
        className={cn(
          'focus-ring ml-auto inline-flex items-center gap-1 rounded uppercase tracking-wide',
          active ? 'text-action' : 'text-ink-muted hover:text-ink-strong',
        )}
      >
        <column.Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {column.label}
        {/* Both states render a chevron so the header keeps its width and the columns cannot shift
            as the ranking moves between them. */}
        {active ? (
          <ChevronDown className="h-3 w-3 shrink-0" aria-hidden />
        ) : (
          <ChevronUp className="h-3 w-3 shrink-0 text-ink-ghost" aria-hidden />
        )}
      </button>
    </th>
  );
}

/** Exported for the test that pins the sort vocabulary against the columns actually rendered. */
export const TOP_POST_COLUMN_KEYS = COLUMNS.map((c) => c.key);
